import { detectArrayChanges } from '@trimble/utilities';
import { Web3DViewerWrapper } from '@trimble/web3d-utils';
import { MeasurementsCapabilityProvider } from '@trimble/web3d-providers';
import { Accessor, createEffect, createRoot, on } from 'solid-js';
import { Web3dTool } from '@trimble/web3d-providers';
import { SharedCapabilityBase } from '../base/shared-capability-base';
import { CapabilityCategory } from '../base/models/capability-category';
import { Tool } from '@technology/web3d';

export abstract class SimpleMeasurementCapabilityBase extends SharedCapabilityBase {
  protected abstract measurementType: Web3dTool;
  public override category: CapabilityCategory = CapabilityCategory.Markup;
  public readonly hasMeasurements: Accessor<boolean>;
  protected effectDispose?: () => void;

  constructor(
    protected readonly measurementsProvider: MeasurementsCapabilityProvider
  ) {
    super(measurementsProvider);

    this.hasMeasurements = this.measurementsProvider.hasItems;
  }

  public startMeasurement(): Promise<void> {
    this.effectDispose?.();
    this.effectDispose = createRoot((dispose) => {
      createEffect(
        on(this.viewerWrappers, (viewerWrappers, prevViewerWrappers) => {
          const newViewerWrappers = detectArrayChanges(
            prevViewerWrappers,
            viewerWrappers
          ).newItems;
          if (!newViewerWrappers.length) {
            return;
          }
          this.activate();
          this.setupWeb3dMeasurementTool(newViewerWrappers);
        })
      );
      return dispose;
    });

    return Promise.resolve();
  }

  protected async setupWeb3dMeasurementTool(
    viewerWrappers: Web3DViewerWrapper[]
  ): Promise<void> {
    for (const viewerWrapper of viewerWrappers) {
      viewerWrapper.viewer.activeTool = this.measurementType;
    }
    return Promise.resolve();
  }

  public stopMeasurement(): Promise<void> {
    this.deactivate();
    return Promise.resolve();
  }

  public override deactivate(): boolean {
    this.effectDispose?.();
    this.effectDispose = undefined;
    for (const viewerWrapper of this.viewerWrappers()) {
      // Only cancel if this capability's tool is still active
      // Prevents overwriting another measurement that activated before this deactivate ran
      if (viewerWrapper.viewer.activeTool === this.measurementType) {
        const tool = viewerWrapper.viewer.tools[this.measurementType] as Tool;
        tool?.cancel();
      }
    }
    return super.deactivate();
  }

  public removeAllMeasurements(): void {
    this.measurementsProvider.removeAllMeasurements();
  }

  public override trackIsEnabled(): void {
    // Maybe we should track if any measurable objects are loaded in viewer?
    this.enable();
    return;
  }

  public override cleanup(): Promise<void> {
    this.effectDispose?.();
    this.effectDispose = undefined;
    return Promise.resolve();
  }
}
